import { ArrowRight, Menu, X } from 'lucide-react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { BrandMark } from '../components/BrandMark'
import { MobileJoinBar } from '../components/MobileJoinBar'
import paszportStamp from '../assets/paszport-dolnego-slaska-stamp.png'
import { MoneyBackGuaranteeBar } from '../components/MoneyBackGuaranteeBar'
import { LaunchPromoCountdown } from '../components/LaunchPromoCountdown'
import { LaunchPromoPrice } from '../components/LaunchPromoPrice'
import { useAuth } from '../hooks/useAuth'
import { useLaunchPromoCountdown } from '../hooks/useLaunchPromoCountdown'
import { useScrollHeaderHide } from '../hooks/useScrollHeaderHide'
import { resolvePublicPassTier } from '../lib/productFlags'
import { NavAboutMenu } from '../components/nav/NavAboutMenu'
import { useT } from '../i18n/t'
import * as profilesApi from '../lib/api/profiles'

type NavItem = readonly [label: string, href: string]

const landingNavItems: NavItem[] = [
  ['nav.places', '/miejsca'],
  ['nav.community', '/spolecznosc'],
  ['nav.faq', '#faq'],
]

const globalNavItems: NavItem[] = [
  ['nav.places', '/miejsca'],
  ['nav.community', '/spolecznosc'],
  ['nav.faq', '/#faq'],
]

/** Tymczasowo ukryty pasek „Nie spodoba Ci się program?…” — ustaw na true, aby przywrócić. */
const SHOW_MONEY_BACK_GUARANTEE_BAR = false

export function HeaderNav() {
  const location = useLocation()
  const isLanding = location.pathname === '/'
  const navItems = isLanding ? landingNavItems : globalNavItems
  const { t } = useT()
  const { user } = useAuth()
  const [isMembershipPaid, setIsMembershipPaid] = useState<boolean | null>(null)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  useEffect(() => {
    if (!user?.id) {
      queueMicrotask(() => setIsMembershipPaid(null))
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const p = await profilesApi.getMyProfile()
        if (cancelled) return
        setIsMembershipPaid(Boolean(p?.membership_paid_at))
      } catch {
        if (!cancelled) setIsMembershipPaid(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [user?.id])

  useEffect(() => {
    queueMicrotask(() => setMobileNavOpen(false))
  }, [location.pathname])

  useEffect(() => {
    if (!mobileNavOpen) return
    const original = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = original
    }
  }, [mobileNavOpen])

  useEffect(() => {
    if (!mobileNavOpen) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMobileNavOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mobileNavOpen])

  const { active: launchPromoActive } = useLaunchPromoCountdown()
  const showConversionBars = isMembershipPaid !== true
  const showLaunchPromoBar = launchPromoActive && showConversionBars
  const joinHref = user?.id && isMembershipPaid ? '/panel' : '/formularz'
  const headerHidden = useScrollHeaderHide({ forceVisible: mobileNavOpen })

  return (
    <>
    <header
      className={[
        'fixed inset-x-0 top-0 z-50 flex flex-col transition-transform duration-300 ease-out motion-reduce:transition-none',
        headerHidden ? '-translate-y-full' : 'translate-y-0',
      ].join(' ')}
    >
      {SHOW_MONEY_BACK_GUARANTEE_BAR && showConversionBars ? (
        <MoneyBackGuaranteeBar />
      ) : null}
      {showLaunchPromoBar ? (
        <Link
          to={joinHref}
          className="flex w-full shrink-0 items-center justify-center border-b border-ink/10 bg-paper/95 px-4 py-2 text-center text-[12px] font-semibold leading-snug text-ink/75 backdrop-blur-md transition hover:bg-ink/[0.04] hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 sm:text-sm"
          aria-label={t('promo.launchBar.aria')}
        >
          <span className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
            <LaunchPromoCountdown variant="bar" />
            <LaunchPromoPrice tier={resolvePublicPassTier('full')} />
          </span>
        </Link>
      ) : null}
      <div className="w-full border-b border-ink/10 bg-paper/95 backdrop-blur-md">
        <div className="container-x flex h-14 items-center justify-between gap-3 md:h-16 md:gap-4">
          <Link
            to={isLanding ? '/#start' : '/'}
            className="shrink-0 rounded-lg px-2 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
          >
            <BrandMark />
          </Link>

          <nav className="hidden min-w-0 flex-1 items-center justify-center gap-0.5 lg:flex" aria-label="Nawigacja">
            <NavAboutMenu variant="desktop" />
            {navItems.map(([key, href]) =>
              href.startsWith('#') ? (
                <a
                  key={href}
                  href={href}
                  className="rounded-lg px-2.5 py-1.5 text-sm font-semibold text-ink/70 transition hover:bg-ink/5 hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 lg:px-3"
                >
                  {t(key)}
                </a>
              ) : (
                <Link
                  key={href}
                  to={href}
                  className="rounded-lg px-2.5 py-1.5 text-sm font-semibold text-ink/70 transition hover:bg-ink/5 hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 lg:px-3"
                >
                  {t(key)}
                </Link>
              ),
            )}
          </nav>

          <div className="relative z-50 flex shrink-0 items-center gap-1 md:gap-2">
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              className="grid h-9 w-9 place-items-center rounded-full text-ink transition hover:bg-ink/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 lg:hidden"
              aria-label={t('nav.menu.open')}
              aria-expanded={mobileNavOpen}
            >
              <Menu className="h-5 w-5" aria-hidden />
            </button>
            <Link
              to={joinHref}
              className="join-cta-primary inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg bg-brand px-3 py-2 text-xs font-extrabold text-brand-ink shadow-[0_10px_28px_rgba(241,201,1,0.35)] transition hover:-translate-y-0.5 hover:brightness-[1.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 sm:gap-2 sm:px-4 sm:py-2.5 sm:text-sm md:px-5 md:py-3 md:text-base"
            >
              {user?.id && isMembershipPaid ? t('nav.panel') : t('nav.join')}{' '}
              <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden />
            </Link>
          </div>
        </div>
      </div>
    </header>

      <div
        className={[
          'fixed inset-0 z-[1100] lg:hidden',
          mobileNavOpen ? 'pointer-events-auto' : 'pointer-events-none',
        ].join(' ')}
        aria-hidden={!mobileNavOpen}
      >
        <button
          type="button"
          onClick={() => setMobileNavOpen(false)}
          className={[
            'absolute inset-0 bg-ink/35 backdrop-blur-sm transition-opacity duration-300',
            mobileNavOpen ? 'opacity-100' : 'opacity-0',
          ].join(' ')}
          aria-label={t('nav.menu.close')}
        />
        <div
          className={[
            'absolute inset-y-0 right-0 flex w-full max-w-md flex-col bg-paper shadow-[0_0_48px_rgba(35,35,35,0.18)] transition-transform duration-300 ease-out',
            mobileNavOpen ? 'translate-x-0' : 'translate-x-full',
          ].join(' ')}
          role="dialog"
          aria-modal="true"
          aria-label={t('nav.menu.label')}
        >
          <div className="flex items-center justify-between px-6 pt-6">
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-ink/55">
              {t('nav.menu.label')}
            </span>
            <button
              type="button"
              onClick={() => setMobileNavOpen(false)}
              className="grid h-10 w-10 place-items-center rounded-full text-ink transition hover:bg-ink/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
              aria-label={t('nav.menu.close')}
            >
              <X className="h-5 w-5" aria-hidden />
            </button>
          </div>
          <div className="mt-6 flex justify-center px-6">
            <img
              src={paszportStamp}
              alt=""
              aria-hidden="true"
              draggable={false}
              loading="lazy"
              decoding="async"
              className="h-[120px] w-[120px] -rotate-6 select-none object-contain drop-shadow-[0_10px_24px_rgba(0,0,0,0.15)]"
            />
          </div>
          <nav className="mt-6 flex flex-1 flex-col gap-1 px-6" aria-label="Nawigacja">
            <NavAboutMenu variant="mobile" onNavigate={() => setMobileNavOpen(false)} />
            {navItems.map(([key, href]) =>
              href.startsWith('#') ? (
                <a
                  key={href}
                  href={href}
                  onClick={() => setMobileNavOpen(false)}
                  className="group flex items-center justify-between gap-4 border-b border-ink/10 py-4 text-ink/60 transition hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
                >
                  <span className="display text-[28px] leading-none">{t(key)}</span>
                  <span className="h-px w-8 bg-ink/20 transition-all group-hover:bg-ink/60" aria-hidden />
                </a>
              ) : (
                <NavLink
                  key={href}
                  to={href}
                  onClick={() => setMobileNavOpen(false)}
                  className={({ isActive }) =>
                    [
                      'group flex items-center justify-between gap-4 border-b border-ink/10 py-4 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60',
                      isActive ? 'text-ink' : 'text-ink/60 hover:text-ink',
                    ].join(' ')
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span className="display text-[28px] leading-none">{t(key)}</span>
                      <span
                        className={[
                          'h-px w-8 transition-all',
                          isActive ? 'bg-ink' : 'bg-ink/20 group-hover:bg-ink/60',
                        ].join(' ')}
                        aria-hidden
                      />
                    </>
                  )}
                </NavLink>
              ),
            )}
          </nav>

          <div className="border-t border-ink/10 px-6 py-6">
            <Link
                to={joinHref}
                onClick={() => setMobileNavOpen(false)}
              className="join-cta-primary inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-brand px-5 py-3 text-sm font-extrabold text-brand-ink shadow-[0_10px_28px_rgba(241,201,1,0.35)] transition hover:brightness-[1.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
            >
              {user?.id && isMembershipPaid ? t('nav.panel') : t('nav.join')}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </div>
        </div>
      </div>
    <MobileJoinBar />
    </>
  )
}

